import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

import { Modal, Input, Table, Tag, Row, Select, Flex, notification, InputNumber } from 'antd';
import { ColumnsType } from 'antd/es/table';

import {
    LoadingOutlined,
    ReloadOutlined,
    EditOutlined,
    DeleteOutlined,
    PlusOutlined,
    QuestionCircleOutlined,
    CloudUploadOutlined,
    CheckOutlined,
    BuildOutlined,
} from '@ant-design/icons';

import ConsignmentCard, { IBoxGroupCardItem } from '@components/consignment-card/consignment-card';

import Tags from '@controls/tags/tags';
import Toolbar from '@controls/toolbar/toolbar';
import FormHeader from '@controls/form-header/form-header';
import Filter from '@controls/filter/filter';
import Comment from '@controls/comment/comment';

import { exception, securityRestriction, warning } from '@extensions/notification';
import { userLoaded, setFilter } from '@store/actions';
import { useAppDispatch, useAppSelector } from '@store/hooks';
import { delayAction } from '@extensions/utils';

import { serverFetch } from '@flc/server';
import { ITruck } from '@entities/truck';
import { IConsignment } from '@entities/consignment';
import { IUserSession } from '@entities/user-session';
import { ICountry } from '@entities/country';
import { IConsignmentFilter } from '@entities/consignment-filter';
import { ICity } from '@entities/city';

import { Permission, hasPermission } from '@enums/permission';
import { TruckStatus, enumLabel as truckStatusLabel } from '@enums/truck-status';
import { UnitType } from '@core/enums/unit-type';

dayjs.extend(utc);

const filterContext: string = 'ImportConsignments';

const Consignments = () => {
    const { truckId } = useParams();

    const initFilter: IConsignmentFilter = { truckIds: [truckId as string], includePackages: true, checkBoxNumbersExists: true };

    const d = useAppDispatch();
    const navigate = useNavigate();

    const [api, contextHolder] = notification.useNotification();
    const [modal, modalCntextHolder] = Modal.useModal();

    const fileInputRef = useRef(null);

    const userSession = useAppSelector<IUserSession>((s) => s.userSession);
    const filter = useAppSelector<IConsignmentFilter>((s) => s.filters[filterContext]);

    const [truck, setTruck] = useState<ITruck>();
    const [consignments, setConsignments] = useState<Array<IConsignment>>([]);
    const [selectedIds, setSelectedIds] = useState<React.Key[]>([]);
    const [currentConsignment, setCurrentConsignment] = useState<IConsignment>();
    const [countries, setCountries] = useState<Array<ICountry>>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [cities, setCities] = useState<Array<ICity>>([]);
    const [showFilter, setShowFilter] = useState<boolean>(true);

    const [refreshRequired, setRefreshRequired] = useState<boolean>(false);

    useEffect(() => {
        let cleanup = false;

        const fetchData = async () => {
            setLoading(true);

            const promises = [
                await serverFetch('countries', { method: 'GET' })
                    .then((data) => {
                        return data;
                    })
                    .catch((ex) => {
                        exception(api, 'Ошибка получения стран', ex, () => d(userLoaded(undefined)));
                    }),

                await serverFetch(`trucks/${truckId}`, { method: 'GET' })
                    .then((data) => {
                        return data;
                    })
                    .catch((ex) => {
                        exception(api, 'Ошибка получения машины', ex, () => d(userLoaded(undefined)));
                    }),

                await serverFetch('cities', { method: 'GET' })
                    .then((data) => {
                        return data;
                    })
                    .catch((ex) => {
                        exception(api, 'Ошибка получения городов', ex, () => d(userLoaded(undefined)));
                    }),
            ];

            Promise.all([promises]).then((result) => {
                if (cleanup) return;

                setCountries(result[0][0].filter((c: ICountry) => c.generateTariff));
                setTruck(result[0][1]);
                setCities(result[0][2]);

                setLoading(false);
                setRefreshRequired(false);
            });
        };

        fetchData();

        return () => {
            cleanup = true;
        };
    }, []);

    useEffect(() => {
        if (!refreshRequired) return;

        setSelectedIds([]);
        setCurrentConsignment(undefined);

        setRefreshRequired(false);
        setLoading(true);

        serverFetch(`consignments`, { method: 'GET', queryParams: filter })
            .then((data) => {
                setConsignments(data);
                setLoading(false);
            })
            .catch((ex) => {
                exception(api, 'Ошибка получения груза', ex, () => d(userLoaded(undefined)));
                setLoading(false);
            });
    }, [refreshRequired]);

    useEffect(() => {
        if (!filter || !filter.truckIds?.find((t) => t == truckId)) {
            d(setFilter(initFilter, filterContext));

            return;
        }

        delayAction(() => setRefreshRequired(true));
    }, [filter]);

    const onDelete = () => {
        if (!currentConsignment) return;

        serverFetch(`consignments/${currentConsignment.id}`, { method: 'DELETE' })
            .then(() => {
                onSelectChange([]);
                setRefreshRequired(true);
            })
            .catch((ex) => {
                exception(api, 'Ошибка удаления груза', ex, () => d(userLoaded(undefined)));
            });
    };

    const renderToolbar = () => {
        return (
            <Toolbar
                commands={[
                    {
                        label: 'Обновить',
                        key: 'refresh',
                        disabled: loading || !truckId,
                        icon: <ReloadOutlined />,
                        onClick: () => {
                            setRefreshRequired(true);
                        },
                    },
                    {
                        label: 'Добавить партию',
                        disabled:
                            !truckId ||
                            ((truck?.status == TruckStatus.Finished || truck?.status == TruckStatus.Cancelled) &&
                                !hasPermission(userSession.permissions, Permission.FullAccess)),
                        key: 'add',
                        icon: <PlusOutlined />,
                        onClick: () => {
                            if (!hasPermission(userSession.permissions, Permission.ManageTruckConsignment)) {
                                securityRestriction(api, [Permission.ManageTruckConsignment]);
                                return;
                            }

                            navigate(`/flc/logistics/import/consignments/new/${truckId}`);
                        },
                    },
                    {
                        label: 'Добавить из предзагрузки',
                        disabled:
                            !truckId ||
                            ((truck?.status == TruckStatus.Finished || truck?.status == TruckStatus.Cancelled) &&
                                !hasPermission(userSession.permissions, Permission.FullAccess)),
                        key: 'addPreloading',
                        icon: <BuildOutlined />,
                        onClick: () => {
                            if (!hasPermission(userSession.permissions, Permission.ManageTruckConsignment)) {
                                securityRestriction(api, [Permission.ManageTruckConsignment]);
                                return;
                            }

                            navigate(`/flc/logistics/import/consignments/preloading/${truckId}`);
                        },
                    },
                    {
                        label: 'Изменить партию',
                        key: 'edit',
                        disabled:
                            !currentConsignment ||
                            consignments?.some((e) => e.hasBoxNumbers) ||
                            truck?.status == TruckStatus.Finished ||
                            truck?.status == TruckStatus.Cancelled,
                        icon: <EditOutlined />,
                        onClick: () => {
                            navigate(`/flc/logistics/import/consignments/${truckId}/${currentConsignment?.id}`);
                        },
                    },
                    {
                        label: 'Удалить партию',
                        key: 'delete',
                        disabled:
                            !currentConsignment ||
                            consignments?.some((e) => e.hasBoxNumbers) ||
                            truck?.status == TruckStatus.Finished ||
                            truck?.status == TruckStatus.Cancelled,
                        icon: <DeleteOutlined />,
                        onClick: () => {
                            if (!hasPermission(userSession.permissions, Permission.DeleteTruckConsignment)) {
                                securityRestriction(api, [Permission.DeleteTruckConsignment]);
                                return;
                            }

                            modal.confirm({
                                title: `Удалить партию "${currentConsignment?.markingCode}"?`,
                                icon: <QuestionCircleOutlined />,
                                okType: 'primary',
                                okText: 'Удалить',
                                cancelText: 'Отмена',
                                onOk: () => {
                                    onDelete();
                                },
                            });
                        },
                    },
                    {
                        label: 'Импорт',
                        key: 'import',
                        icon: <CloudUploadOutlined />,
                        disabled: truck?.status !== TruckStatus.New,
                        onClick: () => {
                            if (!hasPermission(userSession.permissions, Permission.ManageTruckConsignment)) {
                                securityRestriction(api, [Permission.ManageTruckConsignment]);
                                return;
                            }

                            if (fileInputRef && fileInputRef.current) {
                                const current = fileInputRef.current as HTMLInputElement;
                                current.click();
                            }
                        },
                    },
                    {
                        label: 'Завершить погрузку',
                        disabled: truck?.status !== TruckStatus.New,
                        key: 'complete',
                        type: 'primary',
                        icon: <CheckOutlined />,
                        onClick: () => {
                            if (!hasPermission(userSession.permissions, Permission.CompleteTruckLoading)) {
                                securityRestriction(api, [Permission.CompleteTruckLoading]);
                                return;
                            }

                            modal.confirm({
                                title: `Завершить погрузку?`,
                                icon: <QuestionCircleOutlined />,
                                okType: 'primary',
                                okText: 'ОК',
                                cancelText: 'Отмена',
                                onOk: () => {
                                    onCompleteLoading();
                                },
                            });
                        },
                    },
                ]}
                filter={{
                    active: showFilter,
                    onHideFilter: () => {
                        setShowFilter(!showFilter);
                    },
                }}
            />
        );
    };

    const onCompleteLoading = () => {
        setLoading(true);

        serverFetch(`trucks/completeloading/${truckId}`, { method: 'POST' })
            .then(() => {
                setLoading(false);
                navigate(-1);
            })
            .catch((ex) => {
                setLoading(false);
                exception(api, 'Ошибка завершения погрузки', ex, () => d(userLoaded(undefined)));
            });
    };

    const onSelectChange = (selectedRowKeys: React.Key[]) => {
        setSelectedIds(selectedRowKeys);

        if (selectedRowKeys.length == 1) {
            const entity = consignments.find((o) => o.id === selectedRowKeys[0]);
            setCurrentConsignment(entity);
        } else {
            setCurrentConsignment(undefined);
        }
    };

    const renderFilter = () => {
        return (
            <Filter
                display={showFilter}
                items={[
                    <InputNumber
                        style={{ width: 130 }}
                        key='consigneeCode'
                        placeholder='ID клиента'
                        value={filter?.consigneeCode}
                        onChange={(value) => {
                            d(setFilter({ ...filter, consigneeCode: value }, filterContext));
                        }}
                    />,
                    <Input
                        style={{ width: 130 }}
                        key='markingCode'
                        placeholder='Маркировка'
                        value={filter?.markingCode}
                        onChange={(data: React.ChangeEvent<HTMLInputElement>) => {
                            d(setFilter({ ...filter, markingCode: data.target.value }, filterContext));
                        }}
                    />,
                    <Input
                        style={{ width: 130 }}
                        key='awbNumber'
                        placeholder='AWB'
                        value={filter?.awbNumber}
                        onChange={(data: React.ChangeEvent<HTMLInputElement>) => {
                            d(setFilter({ ...filter, awbNumber: data.target.value }, filterContext));
                        }}
                    />,
                    <Select
                        key='city'
                        placeholder='Город доставки'
                        value={filter?.cityIds}
                        size='middle'
                        mode='multiple'
                        maxTagCount='responsive'
                        allowClear
                        style={{ width: 200 }}
                        onChange={(value) => d(setFilter({ ...filter, cityIds: value }, filterContext))}
                        filterOption={(input, option) => (option?.label as string).toLowerCase().includes(input.toLowerCase())}
                        filterSort={(a, b) => (a?.label as string).toLowerCase().localeCompare((b?.label as string).toLowerCase())}
                        options={cities.map((s) => {
                            return { value: s.id, label: s.name };
                        })}
                    />,
                ]}
                onClear={() => d(setFilter(initFilter, filterContext))}
            />
        );
    };

    const renderTable = () => {
        const lines = consignments.map((l) => {
            const boxGroups: Array<any> = [];
            let totalQty: number = 0;
            let grossWeight: number = 0;
            let volumeWeight: number = 0;
            let totalVolume: number = 0;

            l.boxGroups?.map((b) => {
                boxGroups[b.countryId as any] = { qty: b.qty, loadingOn: b.loadingOn, awbNumber: b.awbNumber };
                totalQty += b.qty || 0;
                grossWeight += b.grossWeight || 0;
                volumeWeight += b.volumeWeight || 0;
                totalVolume += b.calcVolume || 0;
            });

            l = {
                ...l,
                ...boxGroups,
                totalQty: totalQty,
                grossWeight: grossWeight,
                volumeWeight: volumeWeight,
                totalVolume: totalVolume,
            };
            return l;
        });

        const columns: ColumnsType<IConsignment> = [
            {
                title: 'ID',
                align: 'center',
                dataIndex: 'consigneeCode',
                width: 60,
            },
            {
                width: 40,
                align: 'center',
                render: (_, record) => {
                    return record.comment && <Comment comment={record.comment} />;
                },
            },
            {
                title: 'Маркировка',
                dataIndex: 'markingCode',
                width: 200,
            },
            {
                title: 'Город доставки',
                dataIndex: 'cityName',
                width: 200,
                render: (_, record) => {
                    return (
                        <Flex>
                            <span style={{ marginRight: 10 }}>{record.cityName}</span>
                            <Tags label={record.tag} />
                        </Flex>
                    );
                },
            },
            {
                title: 'Кол-во / шт',
                align: 'center',
                dataIndex: 'totalQty',
                width: 120,
                onCell: () => ({
                    style: {
                        background: '#fafafa',
                        fontWeight: 600,
                    },
                }),
            },
            {
                title: 'Брутто / кг',
                align: 'center',
                dataIndex: 'grossWeight',
                width: 120,
                onCell: () => ({
                    style: {
                        background: '#fafafa',
                        fontWeight: 600,
                    },
                }),
            },
            {
                title: 'Объемный / кг',
                align: 'center',
                dataIndex: 'volumeWeight',
                width: 120,
                onCell: () => ({
                    style: {
                        background: '#fafafa',
                        fontWeight: 600,
                    },
                }),
            },
            {
                title: (
                    <>
                        Объем / м<sup>3</sup>
                    </>
                ),
                align: 'center',
                width: 120,
                onCell: () => ({
                    style: {
                        background: '#fff8d5',
                        fontWeight: 600,
                    },
                }),
                render: (_, record) => {
                    return record.totalVolume?.toFixed(2);
                },
            },
            {
                dataIndex: 'countries',
                width: 375,
                render: (_, record) => {
                    return (
                        <Flex gap='small' vertical>
                            {countries.map((c) => {
                                const boxGroups = record.boxGroups?.filter((bg) => bg.countryId === c.id);
                                if (!boxGroups || boxGroups.length <= 0) return undefined;

                                return (
                                    <ConsignmentCard
                                        country={c}
                                        api={api}
                                        items={boxGroups as Array<IBoxGroupCardItem>}
                                        serverFetch={serverFetch}
                                    />
                                );
                            })}
                        </Flex>
                    );
                },
            },
            {
                title: 'Упаковка',
                width: 400,
                onCell: () => ({
                    style: {
                        padding: 0,
                    },
                }),
                render: (_: any, record) => {
                    if (!record.boxGroups) return;

                    return record.boxGroups.map((bg) => {
                        return bg.unitType == UnitType.Cart ? (
                            <Tag key={bg.id} color='#780606' style={{ fontSize: 14, marginLeft: 5, marginTop: 5, marginBottom: 5 }}>
                                Телега
                            </Tag>
                        ) : bg.unitType == UnitType.Pallet ? (
                            bg.packages?.map((p) => {
                                return (
                                    <Tag
                                        key={p.templateId}
                                        color='green'
                                        style={{ fontSize: 14, marginLeft: 5, marginTop: 5, marginBottom: 5, color: '#000000' }}
                                    >
                                        <span style={{ fontWeight: 600 }}>{p.templateName}</span>
                                        <span> : {p.boxQty}</span>
                                        {p.comment && (
                                            <span style={{ marginLeft: 5 }}>
                                                <Comment comment={p.comment} size='middle' />
                                            </span>
                                        )}
                                    </Tag>
                                );
                            })
                        ) : (
                            <></>
                        );
                    });
                },
            },
            {},
        ];

        return (
            <Table
                rowKey='id'
                size='small'
                rowSelection={{
                    selectedRowKeys: selectedIds,
                    onChange: onSelectChange,
                }}
                columns={columns}
                dataSource={lines}
                loading={{
                    spinning: loading,
                    indicator: <LoadingOutlined style={{ fontSize: 44 }} spin />,
                }}
                onRow={(record) => {
                    return {
                        onClick: (event) => {
                            onSelectChange([record.id || '']);
                        },
                    };
                }}
                pagination={false}
                scroll={{ y: `calc(100vh - ${showFilter ? '340px' : '282px'})`, x: 'max-content' }}
                summary={(pageData) => {
                    let qty = 0;
                    let totalGrossWeight = 0;
                    let totalVolumeWeight = 0;
                    let volume = 0;
                    pageData.forEach(({ totalQty, grossWeight, volumeWeight, totalVolume }) => {
                        qty += totalQty || 0;
                        totalGrossWeight += grossWeight || 0;
                        totalVolumeWeight += volumeWeight || 0;
                        volume += totalVolume || 0;
                    });
                    return (
                        <Table.Summary fixed>
                            <Table.Summary.Row>
                                <Table.Summary.Cell index={0} colSpan={5} align='left'>
                                    ИТОГО
                                </Table.Summary.Cell>
                                <Table.Summary.Cell index={5} align='center'>
                                    {qty}
                                </Table.Summary.Cell>
                                <Table.Summary.Cell index={6} align='center'>
                                    {totalGrossWeight}
                                </Table.Summary.Cell>
                                <Table.Summary.Cell index={7} align='center'>
                                    {totalVolumeWeight}
                                </Table.Summary.Cell>
                                <Table.Summary.Cell index={8} align='center'>
                                    {volume.toFixed(2)}
                                </Table.Summary.Cell>
                                <Table.Summary.Cell index={9}></Table.Summary.Cell>
                                <Table.Summary.Cell index={10}></Table.Summary.Cell>
                            </Table.Summary.Row>
                        </Table.Summary>
                    );
                }}
            />
        );
    };

    return (
        <>
            <Row>
                <FormHeader title={`Управление грузом (${truck?.number} - "${truckStatusLabel(truck?.status)}")`} />
            </Row>
            {renderToolbar()}
            {renderFilter()}
            {renderTable()}

            <input
                type='file'
                accept={'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'}
                onChange={(x) => {
                    if (!x.target || !x.target.files) return;

                    const file = x.target.files[0];
                    if (file) {
                        const reader = new FileReader();
                        reader.readAsDataURL(file);
                        reader.onload = () => {
                            const result = reader?.result as string;

                            const fileBody = result.replace(
                                'data:application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;base64,',
                                ''
                            );

                            setLoading(true);
                            serverFetch(`consignments/${truckId}/import`, { method: 'POST', bodyData: fileBody })
                                .then((messages) => {
                                    setLoading(false);

                                    if (messages && messages.length > 0) {
                                        warning(
                                            api,
                                            'Файл импортирован частично',
                                            <ul style={{ overflow: 'auto', height: 300 }}>
                                                {messages.map((m: any) => {
                                                    return <li>{m}</li>;
                                                })}
                                            </ul>
                                        );
                                    }

                                    setRefreshRequired(true);
                                })
                                .catch((ex) => {
                                    setLoading(false);
                                    exception(api, 'Ошибка ипорта файла', ex, () => d(userLoaded(undefined)));
                                });
                        };

                        reader.onerror = (error) => console.log(error);
                    }
                }}
                onClick={(e) => {
                    const target = e.target as HTMLInputElement;
                    target.value = '';
                }}
                multiple={false}
                ref={fileInputRef}
                style={{ display: 'none' }}
            />
            {contextHolder}
            {modalCntextHolder}
        </>
    );
};

export default Consignments;
